/**
 * The master config moves that need a toast: follow it (one container),
 * delete it (only once no container follows it — Phase 20), and make one
 * out of a container. Shared by the master config card, its empty state and the
 * board's row details.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useState } from 'react';

import { getApiErrorMessage } from '@/lib/utils';
import { useToast } from '@/providers/ToastProvider';

import { useMasterMutations } from '../../../hooks/useMasterMutations';

export function useMasterConfigActions() {
  const { followMaster, removeMaster, createMasterFromContainer } = useMasterMutations();
  const toast = useToast();
  const [following, setFollowing] = useState<string | null>(null);

  const follow = async (containerId: string, masterId: string) => {
    setFollowing(containerId);
    try {
      await followMaster.mutateAsync({ id: containerId, masterId });
      toast.success('Now following the master config', 'Its own values stay saved but no longer apply.');
    } catch (err) {
      toast.error('Could not follow', getApiErrorMessage(err));
    } finally {
      setFollowing(null);
    }
  };

  const remove = async (id: string): Promise<boolean> => {
    try {
      await removeMaster.mutateAsync({ id });
      toast.success('Master config deleted');
      return true;
    } catch (err) {
      toast.error('Could not delete', getApiErrorMessage(err));
      return false;
    }
  };

  const promote = async (container: { id: string; name: string }, name: string): Promise<boolean> => {
    try {
      await createMasterFromContainer.mutateAsync({ containerId: container.id, name });
      toast.success('Master config created', `${container.name} now follows it — nothing changed for today.`);
      return true;
    } catch (err) {
      toast.error('Could not make the master config', getApiErrorMessage(err));
      return false;
    }
  };

  return {
    follow,
    following,
    remove,
    removing: removeMaster.isPending,
    promote,
    promoting: createMasterFromContainer.isPending,
  };
}
